/***
*
*   useWorkspaceManagement hooks
*   Fetch and manage workspace data for admin dashboard
*
**********/

import { useState, useEffect, useCallback } from 'react';
import Axios from 'axios';
import { useAPI } from 'components/lib';

/**
 * Hook to fetch list of workspaces with pagination and filters
 */
export function useWorkspaces(filters = {}) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [reload, setReload] = useState(0);

  const fetchWorkspaces = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.search) params.append('search', filters.search);
      if (filters.plan) params.append('plan', filters.plan);
      if (filters.status) params.append('status', filters.status);
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.order) params.append('order', filters.order);

      const response = await Axios.get(`/api/dashboard/workspaces?${params.toString()}`);
      setState({ data: response.data.data, loading: false, error: null });
    } catch (err) {
      setState({ data: null, loading: false, error: err.message });
    }
  }, [filters.page, filters.limit, filters.search, filters.plan, filters.status, filters.sort, filters.order]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces, reload]);

  return { ...state, refetch: () => setReload(r => r + 1) };
}

/**
 * Hook to fetch single workspace details
 */
export function useWorkspaceDetail(id) {
  return useAPI(id ? `/api/dashboard/workspaces/${id}` : null);
}

/**
 * Hook to fetch workspace users
 */
export function useWorkspaceUsers(workspaceId, page = 1, limit = 25) {
  return useAPI(workspaceId ? `/api/dashboard/workspaces/${workspaceId}/users?page=${page}&limit=${limit}` : null);
}

/**
 * Hook to fetch workspace employees
 */
export function useWorkspaceEmployees(workspaceId, page = 1, limit = 25) {
  return useAPI(workspaceId ? `/api/dashboard/workspaces/${workspaceId}/employees?page=${page}&limit=${limit}` : null);
}

/**
 * Hook to fetch workspace billing
 */
export function useWorkspaceBilling(workspaceId) {
  return useAPI(workspaceId ? `/api/dashboard/workspaces/${workspaceId}/billing` : null);
}

/**
 * Hook to fetch workspace feature usage
 */
export function useWorkspaceFeatures(workspaceId) {
  return useAPI(workspaceId ? `/api/dashboard/workspaces/${workspaceId}/features` : null);
}

/**
 * Hook for workspace mutations (activate/deactivate)
 */
export function useWorkspaceMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const deactivate = useCallback(async (workspaceId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.post(`/api/dashboard/workspaces/${workspaceId}/deactivate`);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to deactivate workspace');
      setLoading(false);
      throw err;
    }
  }, []);

  const activate = useCallback(async (workspaceId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.post(`/api/dashboard/workspaces/${workspaceId}/activate`);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to activate workspace');
      setLoading(false);
      throw err;
    }
  }, []);

  const exportWorkspace = useCallback(async (workspaceId) => {
    try {
      const response = await Axios.get(`/api/dashboard/workspaces/${workspaceId}/export`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `workspace-${workspaceId}-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      throw new Error('Failed to export workspace data');
    }
  }, []);

  const exportAll = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.plan) params.append('plan', filters.plan);
      if (filters.status) params.append('status', filters.status);

      const response = await Axios.get(`/api/dashboard/workspaces-export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all-workspaces-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      throw new Error('Failed to export workspaces data');
    }
  }, []);

  return { deactivate, activate, exportWorkspace, exportAll, loading, error };
}